// src/utils/tieRules.ts
//
// Pravidla LKS pro shodu bodů (fáze 4b živého bodování, rozhodnutí majitele
// 8. 10. 2026 – docs/live-faze4-navrh.md bod 6, otázka 3):
//
//  1. O prvních X míst (nastavení závodu `shootOffPlaces`, výchozí 3,
//     správce může zvýšit) rozhoduje ROZSTŘEL – stávající rozstřel s otiskem
//     skupiny (utils/shootOff.ts, resolveShootOff).
//  2. Ostatní shody (a shoda v prvních X bez platného rozstřelu) rozhoduje
//     POČET ZÁSAHŮ nejvyšší hodnoty (countback): víc zásahů 30,1 (srdce),
//     při rovnosti víc zásahů 30, pak další nižší hodnota… Jen kladné
//     hodnoty, z rozpadu na rány (gongy postupně: `shots` 1/0 + `gongValues`;
//     gongy výběr a terče: hodnota každé rány v `shots`). Použije se, jen
//     když má KAŽDÝ ve shodě úplná a souhlasná data na všech nastavených
//     stanovištích (souhlasná = rány dají body stanoviště; oprava jen bodů
//     bez odpovídajících ran = neúplné).
//  3. Když nerozhodne ani počet zásahů: STEJNÉ MÍSTO (2., 2., další 4.),
//     v rámci shody podle startovního čísla.
//
// Platí jen pro závody ukončené od nasazení 4b (LIVE_TIE_FROM_MS) a závody
// neukončené; starší závody si nechávají přesně dosavadní pořadí i místa
// všude (tabule, Živé výsledky, export, zveřejněné výsledky, sdílení, Moje
// sezóna). Rozhoduje neměnné races.finishedAt (jako LIVE_F1_FROM_MS) – bez
// změny pravidel Firestore.
//
// Proč z ran a ne z bodů: 10 srdečních zásahů (10 × 30,1 = 301) se v součtu
// „přelije“ do celých bodů – desetiny součtu počet srdcí neurčí.
//
// Bez `Array.prototype.at`, `structuredClone`, `replaceAll`, `Object.fromEntries`
// v cestách tabule (Chrome 80, iOS 15).

import { timeMillis } from "./livePermissions";
import { resolveShootOff, type ShootOffId } from "./shootOff";

// ── Platnost pravidel ─────────────────────────────────────────────────────

/**
 * Od kdy platí pravidla shody 4b: závod ukončený PŘED tímto okamžikem se
 * řadí jako dřív (shoda podle rozstřelu a pořadí obrazovky / dokumentů,
 * místa = pořadí). Hodnota = plánovaný den nasazení 4b (00:00 UTC).
 * Nasazuje-li se později a mezitím se starou verzí ukončil závod, posunout
 * na skutečný den nasazení (do listopadu 2026 se žádný závod LKS nekoná).
 */
export const LIVE_TIE_FROM_MS = Date.UTC(2026, 9, 12);

/** Výchozí počet míst rozhodovaných rozstřelem. */
export const DEFAULT_SHOOT_OFF_PLACES = 3;
/** Nejvyšší rozumné nastavení (formulář závodu). */
export const MAX_SHOOT_OFF_PLACES = 50;

/** Závod pro rozhodnutí o pravidlech shody. */
export interface TieRaceLike {
  status?: unknown;
  finishedAt?: unknown;
  shootOffPlaces?: unknown;
  liveScoring?: unknown;
}

/**
 * Platí pro závod pravidla shody 4b? Neukončený ano (ukončí ho nová verze);
 * ukončený s finishedAt od LIVE_TIE_FROM_MS ano; ukončený dřív nebo bez
 * finishedAt ne. Neznámý závod (null) ne – bezpečnější dosavadní pořadí.
 */
export function liveTieRulesApply(race: TieRaceLike | null | undefined): boolean {
  if (!race) return false;
  if (race.status !== "finished") return true;
  const ms = timeMillis(race.finishedAt);
  return ms !== null && ms > 0 && ms >= LIVE_TIE_FROM_MS;
}

/**
 * Zveřejněné výsledky: pravidla 4b jen u závodu živého bodování (import ani
 * závod bez živého bodování se nemění) a podle liveTieRulesApply. Výsledky
 * navíc musí zveřejnit verze 4b (rankRaceEntries – záznam s polem
 * hitCountsComplete): starší závod s živým bodováním, který nikdo neukončil,
 * nebo import do něj se tak nezmění (nález 1 kontroly 4b).
 */
export function publishedTieRulesApply(race: TieRaceLike | null | undefined): boolean {
  return !!race && race.liveScoring === true && liveTieRulesApply(race);
}

/** Počet míst rozhodovaných rozstřelem: celé číslo 1…50, jinak výchozí 3. */
export function shootOffPlacesOf(race: TieRaceLike | null | undefined): number {
  const v = race ? race.shootOffPlaces : undefined;
  return typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= MAX_SHOOT_OFF_PLACES
    ? v
    : DEFAULT_SHOOT_OFF_PLACES;
}

/**
 * Počet míst z formuláře závodu: celé číslo 4…50 se uloží; prázdné, 3
 * a menší (výchozí – „správce může zvýšit“) nebo neplatné → null = pole se
 * nezapisuje / smaže (platí výchozí 3).
 */
export function shootOffPlacesFromForm(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isInteger(n) || n <= DEFAULT_SHOOT_OFF_PLACES || n > MAX_SHOOT_OFF_PLACES) return null;
  return n;
}

/** Nastavení řazení obrazovky: null = dosavadní pořadí (starší závod). */
export interface TieRulesConfig {
  shootOffPlaces: number;
}

/** Pravidla shody pro závod živého bodování (tabule, Živé výsledky, publikace). */
export function tieRulesOf(race: TieRaceLike | null | undefined): TieRulesConfig | null {
  return liveTieRulesApply(race) ? { shootOffPlaces: shootOffPlacesOf(race) } : null;
}

// ── Zásahy podle hodnot ───────────────────────────────────────────────────

/** Počet zásahů jedné hodnoty (kladné body jedné rány). */
export interface HitCount {
  value: number;
  count: number;
}

/** Klíč hodnoty v setinách (30,1 → 3010) – bez float šumu. */
const hitKey = (value: number): number => Math.round(value * 100);
/** Tolerance součtu ran proti bodům stanoviště (body mají nejvýš 2 des. místa). */
const SUM_TOLERANCE = 0.005;

const finiteNumbers = (value: unknown): number[] | null => {
  if (!Array.isArray(value)) return null;
  for (let i = 0; i < value.length; i++) {
    if (typeof value[i] !== "number" || !Number.isFinite(value[i])) return null;
  }
  return value as number[];
};

/** Stanoviště tak, jak ho čte rozpad na rány (LiveStation). */
export interface HitStationLike {
  id: string;
  type?: unknown;
  config?: unknown;
}

/** Hodnoty gongů z nastavení stanoviště v pořadí nastavení (záloha pro starší zápis bez gongValues). */
function configGongValues(config: unknown): number[] {
  const gongs = config && typeof config === "object" ? (config as { gongs?: unknown }).gongs : undefined;
  if (!Array.isArray(gongs)) return [];
  const out: number[] = [];
  gongs.forEach((g) => {
    const v = g && typeof g === "object" ? (g as { value?: unknown }).value : undefined;
    if (typeof v === "number" && Number.isFinite(v)) out.push(v);
  });
  return out;
}

/**
 * Body jednotlivých ran stanoviště z uloženého zápisu, nebo null, když rozpad
 * chybí nebo neodpovídá bodům stanoviště (oprava jen bodů, terč zadaný
 * součtem před 4b, poškozená data):
 *  - gongy postupně: `shots` 1/0 po gongách, hodnota rány i = gongValues[i]
 *    (za koncem poslední gong – jako obrazovka rozhodčího); bez gongValues
 *    hodnoty z nastavení stanoviště,
 *  - gongy výběr a terče: `shots` = body každé rány (0 = minul; terč může
 *    mít i záporné zóny).
 */
export function stationShotValues(station: HitStationLike | undefined, entry: unknown): number[] | null {
  if (!station || !entry || typeof entry !== "object") return null;
  const e = entry as { score?: unknown; shots?: unknown; gongValues?: unknown };
  if (typeof e.score !== "number" || !Number.isFinite(e.score)) return null;
  const shots = finiteNumbers(e.shots);
  if (!shots || shots.length === 0) return null;
  let values: number[];
  if (station.type === "gongs_sequential") {
    const stored = finiteNumbers(e.gongValues);
    const gv = stored && stored.length > 0 ? stored : configGongValues(station.config);
    if (gv.length === 0) return null;
    values = [];
    for (let i = 0; i < shots.length; i++) {
      const s = shots[i];
      if (s !== 0 && s !== 1) return null;
      values.push(s === 1 ? (i < gv.length ? gv[i] : gv[gv.length - 1]) : 0);
    }
  } else if (station.type === "gongs_choice" || station.type === "target") {
    values = shots.slice();
  } else {
    return null;
  }
  let sum = 0;
  values.forEach((v) => {
    sum += v;
  });
  return Math.abs(sum - e.score) < SUM_TOLERANCE ? values : null;
}

/** Zásahy (kladné hodnoty) seřazené od nejvyšší hodnoty. */
export function hitCountsOf(values: ReadonlyArray<number>): HitCount[] {
  const map = new Map<number, number>();
  values.forEach((v) => {
    if (!(v > 0)) return;
    const k = hitKey(v);
    map.set(k, (map.get(k) || 0) + 1);
  });
  const out: HitCount[] = [];
  map.forEach((count, k) => out.push({ value: k / 100, count }));
  out.sort((a, b) => b.value - a.value);
  return out;
}

/**
 * Zásahy střelce ze všech NASTAVENÝCH stanovišť, nebo null, když některé
 * stanoviště chybí (nestřílel) nebo nemá souhlasný rozpad na rány – počet
 * zásahů pak o shodě nerozhoduje (stejné místo). Body vyřazených stanovišť
 * se nepočítají (jako součet).
 */
export function liveHitCounts(
  result: { stations?: unknown } | null | undefined,
  stations: ReadonlyArray<HitStationLike>
): HitCount[] | null {
  if (!result || stations.length === 0) return null;
  const byStation = result.stations && typeof result.stations === "object"
    ? (result.stations as Record<string, unknown>)
    : {};
  const all: number[] = [];
  for (let i = 0; i < stations.length; i++) {
    const values = stationShotValues(stations[i], byStation[stations[i].id]);
    if (!values) return null;
    for (let k = 0; k < values.length; k++) all.push(values[k]);
  }
  return hitCountsOf(all);
}

/** Zásahy z results_entries.hitCounts (zveřejněný záznam), null = neplatné / neúplné. */
export function parseHitCounts(value: unknown): HitCount[] | null {
  if (!Array.isArray(value)) return null;
  const values: number[] = [];
  for (let i = 0; i < value.length; i++) {
    const item = value[i] as { value?: unknown; count?: unknown } | null;
    if (!item || typeof item !== "object") return null;
    const v = item.value;
    const c = item.count;
    if (typeof v !== "number" || !Number.isFinite(v) || !(v > 0)) return null;
    if (typeof c !== "number" || !Number.isInteger(c) || c < 0 || c > 100000) return null;
    for (let k = 0; k < c; k++) values.push(v);
  }
  return hitCountsOf(values);
}

/**
 * Countback: kladné = `a` má víc zásahů nejvyšší hodnoty, ve které se liší
 * (30,1 před 30 před 20 …), záporné = `b`, 0 = shodné počty všech hodnot.
 */
export function compareHitCounts(a: ReadonlyArray<HitCount>, b: ReadonlyArray<HitCount>): number {
  const ca = new Map<number, number>();
  const cb = new Map<number, number>();
  const keys: number[] = [];
  a.forEach((h) => {
    const k = hitKey(h.value);
    if (!ca.has(k) && !cb.has(k)) keys.push(k);
    ca.set(k, (ca.get(k) || 0) + h.count);
  });
  b.forEach((h) => {
    const k = hitKey(h.value);
    if (!ca.has(k) && !cb.has(k)) keys.push(k);
    cb.set(k, (cb.get(k) || 0) + h.count);
  });
  keys.sort((x, y) => y - x);
  for (let i = 0; i < keys.length; i++) {
    const d = (ca.get(keys[i]) || 0) - (cb.get(keys[i]) || 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Stejné počty zásahů všech hodnot. */
export const sameHitCounts = (a: ReadonlyArray<HitCount>, b: ReadonlyArray<HitCount>): boolean =>
  compareHitCounts(a, b) === 0;

/** Zápis do results_entries (kopie bez cizích polí, hodnoty na 2 des. místa). */
export function hitCountsForEntry(hits: ReadonlyArray<HitCount>): HitCount[] {
  return hits.map((h) => ({ value: hitKey(h.value) / 100, count: h.count }));
}

/** Krátký text „3× 30,1 · 5× 30 · 2× 20“ (Živé výsledky, nápověda). */
export function hitCountsText(hits: ReadonlyArray<HitCount> | null): string {
  if (!hits) return "";
  if (hits.length === 0) return "bez zásahu";
  return hits.map((h) => `${h.count}× ${String(h.value).replace(".", ",")}`).join(" · ");
}

// ── Pořadí podle pravidel shody ───────────────────────────────────────────

/** Údaje položky pro pořadí (řádek tabule / zveřejněný záznam). */
export interface TieRankInput {
  /** Body (součet nastavených stanovišť / matchPoints). */
  total: number;
  /** Má výsledek – dostává místo; bez výsledku na konec bez místa. */
  ranked: boolean;
  /** Identita pro otisk rozstřelu (startovní číslo – shootOffIdOf). */
  shootOffId: ShootOffId;
  /** Uložený rozstřel (0 = bez). */
  tiebreaker: number;
  /** Zásahy; null = neúplná / nesouhlasná data (countback se nepoužije). */
  hits: ReadonlyArray<HitCount> | null;
  /** Pořadí uvnitř stejného místa (startovní číslo; záznam bez něj za ně). */
  order: number;
}

/** Jak se rozhodla shoda řádku. */
export type TieMethod = "shootOff" | "countback" | "shared";

export interface TieRankInfo {
  /** Místo (1 = první); null = bez výsledku. */
  place: number | null;
  /** Počet střelců se stejnými body (1 = bez shody). */
  tieSize: number;
  /** Místo, na kterém začíná skupina stejných bodů. */
  groupStartPlace: number | null;
  /** Jak se shoda rozhodla; null = bez shody. */
  method: TieMethod | null;
  /** Řádek sdílí místo s jiným. */
  shared: boolean;
  /** Pořadí v rozstřelu (1 = vítěz) u shody rozhodnuté rozstřelem. */
  shootOffRank: number | null;
  /** Skupina je v prvních X místech s body > 0 – o pořadí má rozhodnout rozstřel (R). */
  shootOffEligible: boolean;
}

export interface TieRanking<T> {
  /** Položky v pořadí: s výsledkem podle míst, pak bez výsledku podle `order`. */
  ordered: T[];
  info: Map<T, TieRankInfo>;
}

export interface TieRankOptions<T> {
  shootOffPlaces: number;
  /**
   * Náhradní rozstřel skupiny, když její vlastní není platný – rozstřel
   * většího celku, jehož je skupina částí (kategorie uvnitř celkové shody
   * rozhodnuté celkovým rozstřelem). Vrací platné hodnoty ve stejném pořadí
   * jako `members` (všechny různé a kladné), jinak null.
   */
  fallbackShootOff?: (members: T[]) => number[] | null;
  /**
   * Shoda = přesně stejné body (zveřejněné výsledky – import mívá víc
   * desetinných míst, než se zobrazí); jinak na 2 desetinná místa (živé
   * součty jsou zaokrouhlené na 2 místa – totéž).
   */
  exactTotals?: boolean;
}

const pointsKey = (total: number): number => Math.round(total * 100);

/**
 * Pořadí a místa podle pravidel shody (hlavička souboru). Jedna funkce pro
 * tabuli, Živé výsledky, export i zveřejněné výsledky – vstup je vždy
 * NEFILTROVANÝ seznam rozsahu (celek, nebo jedna kategorie).
 */
export function rankByTieRules<T>(
  items: ReadonlyArray<T>,
  view: (item: T) => TieRankInput,
  options: TieRankOptions<T>
): TieRanking<T> {
  const x = Math.max(1, Math.floor(options.shootOffPlaces));
  const views = new Map<T, TieRankInput>();
  items.forEach((item) => views.set(item, view(item)));
  const v = (item: T) => views.get(item) as TieRankInput;
  const keyOf = options.exactTotals ? (total: number) => total : pointsKey;

  const ranked = items.filter((item) => v(item).ranked);
  const unranked = items.filter((item) => !v(item).ranked);
  ranked.sort((a, b) => keyOf(v(b).total) - keyOf(v(a).total) || v(a).order - v(b).order);
  unranked.sort((a, b) => v(a).order - v(b).order);

  const ordered: T[] = [];
  const info = new Map<T, TieRankInfo>();
  let start = 0;
  while (start < ranked.length) {
    const key = keyOf(v(ranked[start]).total);
    let end = start + 1;
    while (end < ranked.length && keyOf(v(ranked[end]).total) === key) end++;
    const group = ranked.slice(start, end);
    const groupStart = start + 1;
    if (group.length === 1) {
      ordered.push(group[0]);
      info.set(group[0], {
        place: groupStart, tieSize: 1, groupStartPlace: groupStart, method: null,
        shared: false, shootOffRank: null, shootOffEligible: false,
      });
      start = end;
      continue;
    }
    const total = v(group[0]).total;
    const eligible = total > 0 && groupStart <= x;

    // 1) Rozstřel – jen o prvních X míst
    let shootOffValuesOfGroup: number[] | null = null;
    if (eligible) {
      const resolution = resolveShootOff(group.map((m) => v(m).shootOffId), group.map((m) => v(m).tiebreaker));
      if (resolution.state === "decided") shootOffValuesOfGroup = resolution.effective;
      else if (options.fallbackShootOff) shootOffValuesOfGroup = options.fallbackShootOff(group);
    }
    if (shootOffValuesOfGroup) {
      const values = shootOffValuesOfGroup;
      const byShootOff = group
        .map((m, i) => ({ m, val: values[i] }))
        .sort((a, b) => b.val - a.val || v(a.m).order - v(b.m).order);
      byShootOff.forEach((entry, i) => {
        ordered.push(entry.m);
        info.set(entry.m, {
          place: groupStart + i, tieSize: group.length, groupStartPlace: groupStart, method: "shootOff",
          shared: false, shootOffRank: i + 1, shootOffEligible: eligible,
        });
      });
      start = end;
      continue;
    }

    // 2) Počet zásahů – jen když mají všichni ve shodě úplná data
    const allHits = group.every((m) => v(m).hits !== null);
    const sorted = group.slice().sort((a, b) => {
      if (allHits) {
        const c = compareHitCounts(v(b).hits as HitCount[], v(a).hits as HitCount[]);
        if (c !== 0) return c;
      }
      return v(a).order - v(b).order;
    });
    // 3) Stejné místo pro nerozlišené (2., 2., další 4.)
    let i = 0;
    while (i < sorted.length) {
      let j = i + 1;
      if (allHits) {
        while (j < sorted.length && sameHitCounts(v(sorted[i]).hits as HitCount[], v(sorted[j]).hits as HitCount[])) j++;
      } else {
        j = sorted.length;
      }
      const shared = j - i > 1;
      for (let k = i; k < j; k++) {
        ordered.push(sorted[k]);
        info.set(sorted[k], {
          place: groupStart + i, tieSize: group.length, groupStartPlace: groupStart,
          method: shared ? "shared" : "countback", shared, shootOffRank: null, shootOffEligible: eligible,
        });
      }
      i = j;
    }
    start = end;
  }
  unranked.forEach((item) => {
    ordered.push(item);
    info.set(item, {
      place: null, tieSize: 1, groupStartPlace: null, method: null,
      shared: false, shootOffRank: null, shootOffEligible: false,
    });
  });
  return { ordered, info };
}

/** Popis, jak se shoda rozhodla (Živé výsledky, export). */
export function tieMethodText(info: TieRankInfo | undefined): string {
  if (!info || !info.method) return "";
  if (info.method === "shootOff") return "rozstřel";
  if (info.method === "countback") return "počet zásahů";
  return "stejné místo";
}
